// Campaign channel fields — the controls that belong to one channel.
//
// Both message composers use this: the first message on the create form
// and the step editor on the detail page. A message is an email or a
// text, and each hides the other's controls — a subject line means
// nothing to a carrier, and a segment count means nothing to an inbox.
// Hidden rather than removed, so an agent who switches channel and
// switches back still has what they typed.
//
// The counter restates campaigns.ComposeSMS here and smsview.Segments
// through sms_encoding.js. The footer and the author's sign-off arrive as
// data attributes so the values cannot drift; change the Go function and
// this one changes with it.
//
// Targets: [data-channel-fields].

import { composeSMS, smsEncoding } from "./sms_encoding.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const CHANNEL_SMS = "sms";

export function initCampaignChannel(root = document) {
  root.querySelectorAll("[data-channel-fields]").forEach((form) => {
    if (!firstBind(form)) return;

    // The select is the same control in both composers, so the name is
    // the hook. A workspace without a number renders a hidden input
    // instead, which leaves this null and the channel fixed.
    const channel = form.querySelector('select[name="channel"]');
    // The email-only controls sit in more than one place: the subject
    // above the body, the attachments below it.
    const emailOnly = form.querySelectorAll("[data-channel-email]");
    const smsOnly = form.querySelector("[data-channel-sms]");
    const body = form.querySelector('[name="body"]');
    const counter = form.querySelector("[data-channel-segments]");

    const footerText = form.dataset.footerText || "";
    const signOff = form.dataset.signOff || "";
    const { encodedUnits, segmentsFor, singleSize } = smsEncoding(form.dataset);

    // Always counts the footer: dispatch adds the line for anyone hearing
    // from this number for the first time, so the counter shows the
    // longest this text can be rather than the length the checkbox implies.
    const sending = () => composeSMS(body?.value || "", signOff, footerText);

    const render = () => {
      if (!counter) return;

      const text = sending();
      const { units, gsm } = encodedUnits(text);
      const count = segmentsFor(units, gsm);
      const single = singleSize(gsm);
      // Named for the same reason segmentSummary names it: the checkbox
      // no longer moves this number, and a count that ignores a control
      // the agent just clicked reads as broken.
      const counted = !text
        ? ""
        : signOff
          ? " · with the sign-off and the opt-out line"
          : " · with the opt-out line";
      counter.textContent =
        (count > 1
          ? `${units} characters · ${count} messages`
          : `${units}/${single} characters · 1 message`) + counted;
    };

    const sync = () => {
      const isSMS = channel ? channel.value === CHANNEL_SMS : false;
      emailOnly.forEach((el) => {
        el.hidden = isSMS;
      });
      if (smsOnly) smsOnly.hidden = !isSMS;
      render();
    };

    // The counter describes the body field, so a screen reader reaches it
    // on focus. It is deliberately not a live region: it changes on every
    // keystroke, and reciting the character count per keypress is noise.
    if (counter && body) {
      counter.id ||= `${body.id || "body"}-segments`;
      body.setAttribute("aria-describedby", counter.id);
    }

    channel?.addEventListener("change", sync);
    body?.addEventListener("input", render);

    render();
  });
}
